import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FONTE_MONO } from '../paleta';
import { dataHoraCompleta } from '../utils/dataFalha';
import { getHistoricoDaUnidade, type IHistoricoBloco } from '../api';

/**
 * Linha do tempo de UMA empresa num bot, dentro da linha do popup.
 *
 * Pedido do dono (05/10): *"se eu tenho mais de um erro ou alerta ou sucesso
 * para uma empresa, então só junta as linhas em uma só em ordem cronológica
 * mostrando dd/MM/AA HH:mm:ss dos logs"*.
 *
 * SOB DEMANDA: a consulta só dispara quando a linha é expandida (`enabled`), e
 * não no carregamento do popup. Medido, embutir isto no `/resumo` somaria ~1,4 MB
 * a toda abertura da tela para um dado que o usuário vê numa linha específica.
 *
 * TODOS os desfechos, não só os ruins: a rota própria do monitoramento traz `ok`
 * e `skip` também, que é por que a auditoria de erros não servia aqui (medido: a
 * empresa com 14 eventos `skip` recebia zero dela).
 *
 * O AGRUPAMENTO É DO SERVIDOR: cada item que chega já é um bloco de eventos
 * consecutivos e iguais, com `ocorrencias`. O front só desenha — repetir a regra
 * aqui criaria duas réguas para a mesma coisa, que é a família de bug "card e
 * drawer contam coisas diferentes".
 */

/** Tom por desfecho — as mesmas famílias de cor do resto do popup. */
const TOM: Record<string, { ponto: string; rotulo: string; texto: string }> = {
  ok: { ponto: 'oklch(0.62 0.13 155)', rotulo: 'Concluído', texto: 'oklch(0.4 0.06 160)' },
  falha_op: { ponto: 'oklch(0.58 0.17 25)', rotulo: 'Falha', texto: 'oklch(0.42 0.1 25)' },
  retry: { ponto: 'oklch(0.7 0.14 75)', rotulo: 'Retentativa', texto: 'oklch(0.44 0.09 65)' },
  aguarda_humano: {
    ponto: 'oklch(0.7 0.14 75)',
    rotulo: 'Aguarda humano',
    texto: 'oklch(0.44 0.09 65)',
  },
  skip: { ponto: 'oklch(0.62 0.1 250)', rotulo: 'Pendente', texto: 'oklch(0.42 0.07 250)' },
};

const TOM_DESCONHECIDO = {
  ponto: 'oklch(0.72 0.01 160)',
  rotulo: 'Desfecho',
  texto: 'oklch(0.45 0.015 165)',
};

const tomDoStatus = (status: string | null): typeof TOM_DESCONHECIDO =>
  (status && TOM[status]) || TOM_DESCONHECIDO;

/**
 * A frase de tempo do bloco.
 *
 * Um evento só mostra o carimbo; vários mostram a FAIXA, porque é a faixa que
 * responde "desde quando isso vem acontecendo" — que é a pergunta de quem abre o
 * histórico. Carimbos iguais (dois eventos no mesmo segundo) não viram
 * "entre X e X": nesse caso só o carimbo, com a contagem ao lado.
 */
export const frasePeriodo = (b: IHistoricoBloco): string => {
  const ini = dataHoraCompleta(b.primeiro_em);
  const fim = dataHoraCompleta(b.ultimo_em);
  if (b.ocorrencias <= 1 || !ini || !fim || ini === fim) return fim || ini || 'sem data';
  return `entre ${ini} e ${fim}`;
};

interface Props {
  bot: string;
  codiEmp: string;
  /** Janela da TELA (ISO) — o histórico fala do mesmo recorte que o nó. */
  de?: string;
  ate?: string;
  /** `false` enquanto a linha está fechada: a consulta não dispara. */
  aberto: boolean;
}

export const HistoricoDaUnidade: React.FC<Props> = ({ bot, codiEmp, de, ate, aberto }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['orq', 'historico-unidade', bot, codiEmp, de ?? '', ate ?? ''],
    queryFn: () => getHistoricoDaUnidade({ bot, codi_emp: codiEmp, de, ate }),
    enabled: aberto,
    // A linha do tempo de uma janela fechada não muda; a da janela corrente muda
    // devagar (o bot roda em minutos). 60s evita refetch a cada expandir/fechar
    // sem servir dado velho de quem está acompanhando uma execução.
    staleTime: 60_000,
    retry: 1,
  });

  const caixa: React.CSSProperties = {
    margin: '2px 0 0',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px solid oklch(0.93 0.006 160)',
    background: 'oklch(0.985 0.003 160)',
    fontSize: '11.5px',
    color: 'oklch(0.48 0.015 165)',
    textAlign: 'left',
  };

  if (isLoading) return <div style={caixa}>Carregando o histórico…</div>;

  if (isError) {
    // 403 é resposta de REGRA, não defeito: o histórico inteiro é o dado daquela
    // empresa, e quem não tem a empresa na carteira não o abre. Dizer "sem
    // permissão" evita que isso pareça falha da tela.
    const status = (error as { response?: { status?: number } })?.response?.status;
    return (
      <div style={caixa}>
        {status === 403
          ? 'Sem permissão para ver o histórico desta empresa.'
          : 'Não foi possível carregar o histórico agora.'}
      </div>
    );
  }

  if (!data) return null;

  if (data.blocos.length === 0) {
    return <div style={caixa}>Nenhum registro no período desta tela.</div>;
  }

  return (
    <div style={caixa}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '7px',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'oklch(0.55 0.015 165)',
        }}
      >
        <span>Histórico no período</span>
        <span style={{ flex: 'none', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
          {data.total_eventos} evento{data.total_eventos === 1 ? '' : 's'}
          {data.blocos.length !== data.total_eventos && ` · ${data.blocos.length} agrupados`}
        </span>
      </div>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '7px' }}>
        {data.blocos.map(b => {
          const t = tomDoStatus(b.status);
          return (
            <li
              // Carimbo + desfecho + motivo identificam o bloco: dois blocos
              // consecutivos NUNCA têm essa tripla igual (se tivessem, o servidor
              // os teria fundido num só — é a definição de bloco dele).
              key={`${b.primeiro_em ?? ''}|${b.ultimo_em ?? ''}|${b.status ?? ''}|${b.motivo ?? ''}`}
              style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}
            >
              <span
                aria-hidden="true"
                style={{
                  flex: 'none',
                  width: '7px',
                  height: '7px',
                  marginTop: '5px',
                  borderRadius: '50%',
                  background: t.ponto,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'baseline' }}
                >
                  <strong style={{ fontSize: '11.5px', color: t.texto }}>{t.rotulo}</strong>
                  <span style={{ fontFamily: FONTE_MONO, fontSize: '11px', opacity: 0.9 }}>
                    {frasePeriodo(b)}
                  </span>
                  {b.ocorrencias > 1 && (
                    <span
                      title="Ocorrências consecutivas e iguais, agrupadas numa linha"
                      style={{
                        flex: 'none',
                        padding: '1px 6px',
                        borderRadius: '20px',
                        border: '1px solid oklch(0.9 0.008 160)',
                        background: 'oklch(0.97 0.004 160)',
                        fontSize: '10px',
                        fontWeight: 700,
                      }}
                    >
                      {b.ocorrencias}×
                    </span>
                  )}
                </div>
                {b.motivo && (
                  <div style={{ marginTop: '2px', lineHeight: 1.4, color: t.texto }}>
                    {b.motivo}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Corte e recuo de janela NUNCA ficam calados: sem estes avisos a lista
          parece completa quando não é — a família de bug que o `truncado` e o
          `janela_clampada` existem para evitar. */}
      {data.truncado && (
        <div style={{ marginTop: '7px', fontSize: '10.5px', opacity: 0.85 }}>
          Mostrando os eventos mais recentes — esta empresa tem mais registros do que o limite da
          consulta.
        </div>
      )}
      {data.janela_clampada && (
        <div style={{ marginTop: '4px', fontSize: '10.5px', opacity: 0.85 }}>
          O período pedido excedia o limite do histórico e foi recuado.
        </div>
      )}
    </div>
  );
};
